import React, { useRef, useEffect, useState } from 'react';
import {
  Users,
  ShieldCheck,
  CheckCircle,
  Zap,
  Mail,
  Camera
} from 'lucide-react';
import '../styles/AdminFeaturesSection.css';

const FEATURES = [
  {
    id: 'pipeline',
    tag: 'PIPELINE INTELLIGENCE',
    tagColor: '#818cf8', // Indigo
    headline: 'Automate Your Initial Screening.\nScale Your Hiring.',
    body: 'Instantly assess hundreds of applicants without spending hours on phone screens. JozAI conducts initial technical and behavioral interviews, allowing your team to focus only on the top 10% of talent.',
    bullets: [
      'Bulk upload and invite applicant lists',
      'Automated scheduling and follow-ups',
      'Customizable interviews per role',
    ],
    visual: 'pipeline',
  },
  {
    id: 'analytics',
    tag: 'ACTIONABLE INSIGHTS',
    tagColor: '#38bdf8', // Sky blue
    headline: 'Go Beyond the Resume.\nSee the Real Data.',
    body: 'Get comprehensive, multi-dimensional scorecards for every applicant. Compare technical readiness, communication skills, and behavioral traits side-by-side to make confident hiring decisions.',
    bullets: [
      'Standardized 1-100 scoring across all rounds',
      'Side-by-side applicant comparison matrix',
      'Detailed breakdown of strengths and weaknesses',
    ],
    visual: 'analytics',
  },
  {
    id: 'proctoring',
    tag: 'SECURE ASSESSMENTS',
    tagColor: '#34d399', // Emerald
    headline: 'Trust the Process.\nVerify the Talent.',
    body: 'Maintain complete assessment integrity with our advanced AI proctoring. Monitor gaze tracking, detect tab switching, and flag unauthorized assistance automatically.',
    bullets: [
      'Real-time malpractice detection',
      'Environment and secondary-person flagging',
      'Downloadable integrity reports with flagged frames',
    ],
    visual: 'proctoring',
  },
  {
    id: 'customization',
    tag: 'JOB MATCH ENGINE',
    tagColor: '#a78bfa', // Violet
    headline: 'Tailor the Interview to the\nExact Role.',
    body: "Tired of using generic questions? Upload your exact job description and let AI generate targeted interview questions that assess the specific skills required for the open role.",
    bullets: [
      'Upload JDs to auto-generate technical questions',
      'Set custom passing thresholds',
      'Map interviews to your internal competency framework',
    ],
    visual: 'customization',
  }
];

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { setInView(entry.isIntersecting); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const VisualPipeline: React.FC = () => (
  <div className="admin-fs-visual-card">
    <div className="admin-fs-vis-header">
      <div className="admin-fs-vis-dots"><span /><span /><span /></div>
      <span className="admin-fs-vis-title">Applicant Pipeline</span>
      <div className="admin-fs-vis-live"><span className="admin-fs-live-dot" />ACTIVE</div>
    </div>
    <div className="admin-fs-list">
      {([
        { name: 'Alice Smith', role: 'Frontend Engineer', status: 'Interviewing', icon: Users, color: 'var(--iq-landing-primary)' },
        { name: 'Bob Johnson', role: 'Backend Developer', status: 'Completed', icon: CheckCircle, color: 'var(--iq-landing-primary)' },
        { name: 'Charlie Davis', role: 'Data Scientist', status: 'Invited', icon: Mail, color: 'var(--iq-landing-primary)' },
      ]).map(item => (
        <div key={item.name} className="admin-fs-list-item">
          <div className="admin-fs-list-icon" style={{ background: `${item.color}20`, color: item.color }}>
            <item.icon size={16} />
          </div>
          <div className="admin-fs-list-text">
            <div className="admin-fs-list-title">{item.name}</div>
            <div className="admin-fs-list-sub">{item.role}</div>
          </div>
          <div style={{ fontSize: '0.75rem', color: item.color, fontWeight: 600 }}>{item.status}</div>
        </div>
      ))}
    </div>
  </div>
);

const VisualAnalytics: React.FC = () => (
  <div className="admin-fs-visual-card">
    <div className="admin-fs-vis-header">
      <div className="admin-fs-vis-dots"><span /><span /><span /></div>
      <span className="admin-fs-vis-title">Applicant Scorecard</span>
    </div>
    <div className="admin-fs-score-row">
      <div className="admin-fs-score-bar-wrap">
        {([
          { label: 'Overall Score', val: 92, color: 'var(--iq-landing-primary)' },
          { label: 'Technical Depth', val: 88, color: 'var(--iq-landing-primary)' },
          { label: 'Communication', val: 95, color: 'var(--iq-landing-primary)' },
          { label: 'System Design', val: 85, color: 'var(--iq-landing-primary)' },
        ]).map(b => (
          <div key={b.label} className="admin-fs-score-bar">
            <span className="admin-fs-score-label">{b.label}</span>
            <div className="admin-fs-score-track">
              <div className="admin-fs-score-fill" style={{ width: `${b.val}%`, background: b.color }} />
            </div>
            <span className="admin-fs-score-val" style={{ color: b.color }}>{b.val}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const VisualProctoring: React.FC = () => (
  <div className="admin-fs-visual-card">
    <div className="admin-fs-vis-header">
      <div className="admin-fs-vis-dots"><span /><span /><span /></div>
      <span className="admin-fs-vis-title">Live Proctoring Monitor</span>
    </div>
    <div className="admin-fs-chat">
      <div className="admin-fs-list-item" style={{ borderColor: 'var(--iq-landing-bg)' }}>
        <Camera size={20} color="var(--iq-landing-primary)" />
        <div style={{ flex: 1, marginLeft: 10 }}>
          <div className="admin-fs-list-title" style={{ color: 'var(--iq-landing-primary)' }}>Integrity Check Passed</div>
          <div className="admin-fs-list-sub">No anomalies detected during session</div>
        </div>
      </div>
      <div className="admin-fs-list-item">
        <ShieldCheck size={20} color="var(--iq-landing-primary)" />
        <div style={{ flex: 1, marginLeft: 10 }}>
          <div className="admin-fs-list-title">Identity Verified</div>
          <div className="admin-fs-list-sub">Matches profile photo</div>
        </div>
      </div>
    </div>
  </div>
);

const VisualCustomization: React.FC = () => (
  <div className="admin-fs-visual-card">
    <div className="admin-fs-vis-header">
      <div className="admin-fs-vis-dots"><span /><span /><span /></div>
      <span className="admin-fs-vis-title">Job Match Configuration</span>
    </div>
    <div className="admin-fs-chat">
      <div className="admin-fs-bubble">
        <strong>JD Uploaded: Senior React Developer</strong><br />
        Extracting required skills...
      </div>
      <div className="admin-fs-bubble admin-fs-bubble-bot">
        ✓ Identified: React, Redux, TypeScript, Webpack<br />
        ✓ Generated 5 specific technical rounds<br />
        ✓ Set passing threshold to 85%
      </div>
    </div>
  </div>
);

const VISUALS: Record<string, React.FC> = {
  pipeline: VisualPipeline,
  analytics: VisualAnalytics,
  proctoring: VisualProctoring,
  customization: VisualCustomization,
};

const FeaturePanel: React.FC<{ feature: typeof FEATURES[0], index: number }> = ({ feature, index }) => {
  const { ref, inView } = useInView(0.3);
  const VisualComp = VISUALS[feature.visual];
  const isFlip = index % 2 !== 0;

  return (
    <div ref={ref} className={`admin-fs-content-panel ${isFlip ? 'admin-fs-panel-flip' : ''} ${inView ? 'fs-in-view' : ''}`}>
      <div className="admin-fs-panel-visual-wrapper">
        <div className={`admin-fs-panel-visual admin-fs-iso-${isFlip ? 'right' : 'left'}`}>
          <VisualComp />
        </div>
      </div>
      <div className="admin-fs-panel-text">
        <span className="admin-fs-panel-tag" style={{ color: '#ffffff', background: `linear-gradient(135deg, ${feature.tagColor}, ${feature.tagColor}dd)`, boxShadow: `0 4px 12px ${feature.tagColor}40`, border: 'none' }}>
          {feature.tag}
        </span>
        <h3 className="admin-fs-panel-headline" style={{ '--panel-color': feature.tagColor } as React.CSSProperties}>
          {feature.headline.replace('\n', ' ')}
        </h3>
        <p className="admin-fs-panel-body">{feature.body}</p>
        <ul className="admin-fs-panel-bullets">
          {feature.bullets.map((b, i) => (
            <li key={i}>
              <CheckCircle size={15} strokeWidth={2.5} style={{ color: feature.tagColor, flexShrink: 0, marginTop: 2 }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const AdminFeaturesSection: React.FC = () => {
  const { ref: headerRef, inView: headerInView } = useInView(0.2);

  return (
    <section className="admin-fs-section" id="admin-features">
      <div className="admin-fs-bg-orb admin-fs-orb-1" />
      <div className="admin-fs-bg-orb admin-fs-orb-2" />
      <div className="admin-fs-bg-orb admin-fs-orb-3" />

      <div className="admin-fs-inner">
        <div
          ref={headerRef}
          className="admin-fs-section-header"
          style={{ opacity: headerInView ? 1 : 0, transform: headerInView ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.65s ease, transform 0.65s ease' }}
        >
          <div className="admin-fs-header-pill"><Zap size={12} strokeWidth={2.5} />Enterprise Capabilities</div>
          <h2 className="admin-fs-section-title">
            Transform Your Hiring.<br />
            <span className="admin-fs-title-gradient">Powered by Intelligent Automation.</span>
          </h2>
          <p className="admin-fs-section-sub">
            The Joz HRM Corporate gives you unparalleled control and visibility over your hiring pipeline, enabling you to identify top talent faster and with greater confidence.
          </p>
        </div>

        <div className="admin-fs-vertical-stack">
          {FEATURES.map((f, i) => (
            <FeaturePanel key={f.id} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdminFeaturesSection;
